import React, { ReactNode } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { 
  LayoutGrid, 
  Search, 
  Shield, 
  TrendingUp, 
  RefreshCw, 
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react'

interface ModernLayoutProps {
  children: ReactNode
  activePage: 'overview' | 'research' | 'validation' | 'trading'
  onPageChange: (page: 'overview' | 'research' | 'validation' | 'trading') => void
  connectionStatus: 'connected' | 'disconnected' | 'connecting'
  lastUpdate: string | null
  isLoading?: boolean
  error?: string | null
  onRefresh?: () => void
  onRetry?: () => void
}

const navItems = [
  { 
    id: 'overview' as const, 
    label: 'Overview', 
    icon: LayoutGrid, 
    description: 'System status and performance summary' 
  },
  { 
    id: 'research' as const, 
    label: 'Research Hub', 
    icon: Search, 
    description: 'Strategy generation and convergence analysis' 
  },
  { 
    id: 'validation' as const, 
    label: 'Validation Hub', 
    icon: Shield, 
    description: 'Out-of-sample performance verification' 
  },
  { 
    id: 'trading' as const, 
    label: 'Trading Hub', 
    icon: TrendingUp, 
    description: 'Live positions and execution signals' 
  },
]

export function ModernLayout({
  children,
  activePage,
  onPageChange,
  connectionStatus,
  lastUpdate,
  isLoading = false,
  error = null,
  onRefresh,
  onRetry
}: ModernLayoutProps) {
  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <CheckCircle className="w-4 h-4" />
      case 'connecting':
        return <Clock className="w-4 h-4 animate-pulse" />
      default:
        return <AlertCircle className="w-4 h-4" />
    }
  }

  const getConnectionVariant = (): "connected" | "disconnected" | "connecting" => {
    return connectionStatus
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between">
          {/* Logo and Title */}
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-purple-600">
              <span className="text-lg font-bold text-white">α</span>
            </div>
            <div className="hidden md:block">
              <h1 className="text-xl font-semibold">Alpha Discovery</h1>
              <p className="text-xs text-muted-foreground">Autonomous Trading Strategy Research</p>
            </div>
          </div>

          {/* Status and Actions */}
          <div className="flex items-center gap-4">
            {/* Error Display */}
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <span className="hidden sm:block max-w-48 truncate text-sm text-destructive" title={error}>
                  {error}
                </span>
                {onRetry && (
                  <Button variant="destructive" size="sm" onClick={onRetry}>
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}

            {/* Loading Indicator */}
            {isLoading && !error && (
              <div className="flex items-center gap-2 text-sm">
                <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="hidden sm:block text-muted-foreground">Loading...</span>
              </div>
            )}

            {/* Refresh Button */}
            {!isLoading && !error && onRefresh && (
              <Button variant="outline" size="sm" onClick={onRefresh}>
                <RefreshCw className="h-4 w-4" />
                <span className="hidden sm:block ml-2">Refresh</span>
              </Button>
            )}

            {/* Connection Status */}
            <div className="flex items-center gap-2">
              <Badge variant={getConnectionVariant()} className="gap-1">
                {getConnectionIcon()}
                <span className="capitalize">{connectionStatus}</span>
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b bg-muted/40">
        <div className="container">
          <div className="flex space-x-1 overflow-x-auto">
            {navItems.map((item) => {
              const isActive = activePage === item.id
              return (
                <button
                  key={item.id}
                  onClick={() => onPageChange(item.id)}
                  className={cn(
                    "flex items-center gap-3 whitespace-nowrap border-b-2 px-6 py-4 text-sm font-medium transition-all",
                    isActive
                      ? "border-primary text-primary bg-background"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/60"
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <div className="text-left">
                    <div>{item.label}</div>
                    <div className="hidden lg:block text-xs opacity-75">{item.description}</div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="container py-6">
        {children}
      </main>

      {/* Status Footer */}
      <footer className="border-t bg-muted/40 py-3">
        <div className="container flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            <span>Alpha Discovery Dashboard v1.0.0</span>
            <span>•</span>
            <span>Build: {new Date().toISOString().slice(0, 10)}</span>
            {lastUpdate && (
              <>
                <span>•</span>
                <span>Last Update: {new Date(lastUpdate).toLocaleTimeString()}</span>
              </>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className={cn(
                "h-2 w-2 rounded-full",
                connectionStatus === 'connected' ? "bg-green-400" : "bg-red-400"
              )} />
              <span>D1 {connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-400" />
              <span>R2 Connected</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}